/**
 * System Settings Page
 * Super admin page for configuring global system settings
 */

import React, { useState, useEffect } from 'react';
import { Settings, Save, Clock, Network, Zap, Lock, Info, UserCheck, Radio } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  getSystemSettings,
  updateSystemSettings,
  DEFAULT_SETTINGS,
} from '@/services/systemSettingsService';
import type { SystemSettings } from '@simcheck/shared-types';

export function SystemSettingsPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const fmt = useDateFormat();

  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    networkStalenessHours: DEFAULT_SETTINGS.networkStalenessHours,
    networkScanIntervalMinutes: DEFAULT_SETTINGS.networkScanIntervalMinutes,
    maxConcurrentExecutions: DEFAULT_SETTINGS.maxConcurrentExecutions,
    defaultExecutionTimeoutMinutes: DEFAULT_SETTINGS.defaultExecutionTimeoutMinutes,
    lockTtlMinutes: DEFAULT_SETTINGS.lockTtlMinutes,
    lockCleanupIntervalMinutes: DEFAULT_SETTINGS.lockCleanupIntervalMinutes,
    requireApprovalForTrial: true,
    connectivityModesEnabled: true,
  });

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
    }
  }, [isSuperAdmin, navigate]);

  // Load settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await getSystemSettings();
        setSettings(data);
        setFormData({
          networkStalenessHours: data.networkStalenessHours,
          networkScanIntervalMinutes: data.networkScanIntervalMinutes,
          maxConcurrentExecutions: data.maxConcurrentExecutions,
          defaultExecutionTimeoutMinutes: data.defaultExecutionTimeoutMinutes,
          lockTtlMinutes: data.lockTtlMinutes,
          lockCleanupIntervalMinutes: data.lockCleanupIntervalMinutes,
          requireApprovalForTrial: data.requireApprovalForTrial ?? true,
          connectivityModesEnabled: data.connectivityModesEnabled ?? true,
        });
      } catch (error) {
        console.error('Failed to load settings:', error);
        showError('Failed to load system settings');
      } finally {
        setLoading(false);
      }
    }

    if (isSuperAdmin) {
      loadSettings();
    }
  }, [isSuperAdmin]);

  // Check for changes
  useEffect(() => {
    if (!settings) return;

    const changed =
      formData.networkStalenessHours !== settings.networkStalenessHours ||
      formData.networkScanIntervalMinutes !== settings.networkScanIntervalMinutes ||
      formData.maxConcurrentExecutions !== settings.maxConcurrentExecutions ||
      formData.defaultExecutionTimeoutMinutes !== settings.defaultExecutionTimeoutMinutes ||
      formData.lockTtlMinutes !== settings.lockTtlMinutes ||
      formData.lockCleanupIntervalMinutes !== settings.lockCleanupIntervalMinutes ||
      formData.requireApprovalForTrial !== (settings.requireApprovalForTrial ?? true) ||
      formData.connectivityModesEnabled !== (settings.connectivityModesEnabled ?? true);

    setHasChanges(changed);
  }, [formData, settings]);

  const handleChange = (field: keyof typeof formData, value: number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateSystemSettings(formData);
      setSettings(updated);
      setHasChanges(false);
      showSuccess('System settings have been updated successfully');
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      showError(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setFormData({
      networkStalenessHours: settings.networkStalenessHours,
      networkScanIntervalMinutes: settings.networkScanIntervalMinutes,
      maxConcurrentExecutions: settings.maxConcurrentExecutions,
      defaultExecutionTimeoutMinutes: settings.defaultExecutionTimeoutMinutes,
      lockTtlMinutes: settings.lockTtlMinutes,
      lockCleanupIntervalMinutes: settings.lockCleanupIntervalMinutes,
      requireApprovalForTrial: settings.requireApprovalForTrial ?? true,
      connectivityModesEnabled: settings.connectivityModesEnabled ?? true,
    });
    setHasChanges(false);
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              System Settings
            </h1>
            <p className="text-body text-muted-foreground">
              Configure global system-wide settings for the platform
            </p>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <Button variant="outline" onClick={handleReset} disabled={saving}>
                Reset
              </Button>
            )}
            <Button onClick={handleSave} disabled={!hasChanges || saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Network Scanning Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5" />
                  Network Scanning
                </CardTitle>
                <CardDescription>
                  Configure network discovery and availability tracking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="networkStalenessHours">
                      Network Staleness Threshold
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="networkStalenessHours"
                        type="number"
                        min={1}
                        max={168}
                        value={formData.networkStalenessHours}
                        onChange={(e) => handleChange('networkStalenessHours', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">hours</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Network data older than this will be marked as stale (1-168 hours)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="networkScanIntervalMinutes">
                      Network Scan Interval
                      <span className="ml-2 text-xs text-muted-foreground">(informational)</span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="networkScanIntervalMinutes"
                        type="number"
                        min={5}
                        max={60}
                        value={formData.networkScanIntervalMinutes}
                        onChange={(e) => handleChange('networkScanIntervalMinutes', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Edge devices control actual scan timing (default: 10 min)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Execution Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Execution Settings
                </CardTitle>
                <CardDescription>
                  Configure test execution limits and timeouts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxConcurrentExecutions">
                      Max Concurrent Executions
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="maxConcurrentExecutions"
                        type="number"
                        min={1}
                        max={50}
                        value={formData.maxConcurrentExecutions}
                        onChange={(e) => handleChange('maxConcurrentExecutions', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">executions</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Maximum number of flows that can run simultaneously (1-50)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="defaultExecutionTimeoutMinutes">
                      Default Execution Timeout
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="defaultExecutionTimeoutMinutes"
                        type="number"
                        min={1}
                        max={60}
                        value={formData.defaultExecutionTimeoutMinutes}
                        onChange={(e) => handleChange('defaultExecutionTimeoutMinutes', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Default timeout for flow executions (1-60 minutes)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lock Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Resource Locking
                </CardTitle>
                <CardDescription>
                  Configure resource lock behavior for modem allocation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lockTtlMinutes">
                      Lock TTL (Time-to-Live)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="lockTtlMinutes"
                        type="number"
                        min={5}
                        max={60}
                        value={formData.lockTtlMinutes}
                        onChange={(e) => handleChange('lockTtlMinutes', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      How long a resource lock remains valid before auto-expiration (5-60 min)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lockCleanupIntervalMinutes">
                      Lock Cleanup Interval
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="lockCleanupIntervalMinutes"
                        type="number"
                        min={1}
                        max={30}
                        value={formData.lockCleanupIntervalMinutes}
                        onChange={(e) => handleChange('lockCleanupIntervalMinutes', Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      How often to check and clean up expired locks (1-30 min)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Registration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Registration
                </CardTitle>
                <CardDescription>
                  Control how free-trial sign-ups from the marketing site are admitted
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="requireApprovalForTrial">Require approval for free trials</Label>
                    <p className="text-sm text-muted-foreground">
                      When on, trial sign-ups land as <span className="font-medium">pending</span> — the welcome
                      email is held and a super-admin must approve before the 30-day clock and token grant start.
                      Paid purchases always auto-approve.
                    </p>
                  </div>
                  <Switch
                    id="requireApprovalForTrial"
                    checked={formData.requireApprovalForTrial}
                    onCheckedChange={(v) => handleChange('requireApprovalForTrial', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Connectivity modes kill switch (#252) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Radio className="w-5 h-5" />
                  Connectivity modes
                </CardTitle>
                <CardDescription>
                  Platform-wide kill switch for persistent attach / PLMN hunt on private edges
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="connectivityModesEnabled">Allow connectivity modes</Label>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">On</span> by default. Turn off to block every organization
                      from enabling or changing a connectivity mode on its modems (the modem-settings API returns
                      <span className="font-mono"> feature_disabled</span>), regardless of the per-org
                      &quot;Connectivity modes&quot; flag on the Organizations page. Modems already in a mode keep
                      running until an admin turns them off.
                    </p>
                  </div>
                  <Switch
                    id="connectivityModesEnabled"
                    checked={formData.connectivityModesEnabled}
                    onCheckedChange={(v) => handleChange('connectivityModesEnabled', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Last Updated Info */}
            {settings && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                Last updated: {fmt.formatDateTime(settings.updatedAt)}
                {settings.updatedBy && ` by ${settings.updatedBy}`}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
